import React, { useEffect, useState, createElement, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, MessageSquare, Calendar, Mail, Globe, FileText, Home, ChevronDown, Menu, ArrowRight, Shield, Zap } from 'lucide-react';
// --- Translations Dictionary ---
const translations = {
  en: {
    seo: {
      title: 'Order OpenClaw Installation Turnkey — AI Agent Setup for Business',
      description: 'Order OpenClaw installation turnkey for Telegram, WhatsApp, Slack, email, calendar, and files. AI agent setup for business tasks, launch, configuration, and training.',
      h1: 'Order OpenClaw Installation Turnkey',
      h1sub: 'We set up OpenClaw AI agent for Telegram, WhatsApp, Slack, email, calendar, browser, and files tailored to your business tasks.'
    },
    nav: {
      features: 'Features',
      howItWorks: 'How it works',
      pricing: 'Pricing',
      faq: 'FAQ',
      order: 'Order Setup'
    },
    hero: {
      title1: 'Order',
      title2: 'OpenClaw',
      title3: 'Installation Turnkey',
      subtitle: 'Your personal digital employee in Telegram, WhatsApp, or Slack — configured, trained, and ready to work 24/7.',
      btn: 'Order AI Agent Setup'
    },
    whatWeDo: {
      title1: 'You get a ready-to-use AI agent.',
      title2: 'We handle the rest.',
      withoutYou: 'Without you',
      withoutYouDesc: 'Reading complex docs, setting up server environments, messing with API keys, and writing system prompts.',
      we: 'We',
      weDesc: 'Install the core, configure integrations, connect messengers, train the agent for your specific tasks, and test it.',
      you: 'You',
      youDesc: 'Just open Telegram, text your new employee a task — and they get to work.',
      link: 'I want a ready agent'
    },
    features: {
      title1: 'What OpenClaw will do for your business',
      title2: 'right now',
      mail: 'Email Management',
      mailDesc: 'The agent reads, sorts, and drafts replies to routine emails on its own.',
      cal: 'Calendar',
      calDesc: 'Schedules meetings while keeping your availability in mind.',
      web: 'Browser',
      webDesc: 'Searches for info, scrapes websites, and fills out forms.',
      msg: 'Messengers',
      msgDesc: 'AI agent installation for Telegram, WhatsApp, Slack, and Discord. Chat with the agent like a coworker.',
      files: 'Files',
      filesDesc: 'Analyzes PDFs, spreadsheets, and generates reports.',
      home: 'Smart Home',
      homeDesc: 'Controls lights and automation routines.',
      link: 'Order OpenClaw installation'
    },
    how: {
      title1: 'From request to a working agent —',
      title2: 'turnkey',
      s1: 'You leave a request',
      s1d: 'Fill out a short form or text us on Telegram.',
      s2: 'Free consultation',
      s2d: 'We hop on a call to understand your tasks and workflows.',
      s3: 'OpenClaw setup for business',
      s3d: 'We deploy OpenClaw, connect the required channels, and train the agent.',
      s4: 'Handover & Onboarding',
      s4d: 'We hand over the ready agent, show you how to use it, and provide 30 days of support.',
      link: 'Order Setup'
    },
    pricing: {
      title1: 'One solution. One payment.',
      title2: 'Your agent forever.',
      free: 'Free',
      freeDesc: 'consultation and process audit',
      f1: 'Call with an expert',
      f2: 'Analysis of your routine tasks',
      f3: 'Selection of optimal AI use cases',
      f4: 'Technical feasibility assessment',
      f5: 'Answers to all security questions',
      btnFree: 'Book a call',
      paid: 'Turnkey Setup',
      paidFrom: 'from',
      paidDesc: 'one-time, no hidden subscriptions',
      p1: 'Full OpenClaw installation on your server/PC',
      p2: 'Configuration for your specific business needs',
      p3: 'Messenger integration (Telegram / WhatsApp)',
      p4: 'Installation and setup of required Skills',
      p5: 'Onboarding — you know how to use it from day one',
      p6: '30 days of tech support post-installation',
      btnPaid: 'Order Setup'
    },
    testimonials: {
      title: 'What OpenClaw users say about their agent',
      link: "Want the same? We'll set one up for you.",
      t1: 'After years of AI hype... AI as teammate, not tool. Finally something that actually does the work instead of just generating text.',
      a1: 'Michael',
      h1: '@michael_dev',
      t2: 'No more need to pay a virtual assistant!! OpenClaw handles my emails and scheduling flawlessly. Best investment this year.',
      a2: 'Anna',
      h2: '@anna_biz',
      t3: "Everything Siri was supposed to be. I just text my agent on Telegram and things get done. It's magic.",
      a3: 'Alex',
      h3: '@alex_builds'
    },
    faq: {
      title: 'Frequently Asked Questions',
      link: 'Still have questions? Order Setup',
      q1: 'Is it safe? Will my data leak?',
      a1: "OpenClaw runs locally or on your private server. Data never leaves your perimeter and isn't used to train third-party models.",
      q2: 'Do I need an OpenClaw subscription?',
      a2: 'No. OpenClaw is a completely free open-source project. You only pay for the AI API usage (pennies) and our one-time setup fee.',
      q3: 'Why not set it up myself?',
      a3: "You could. But that's 10-40 hours of figuring out docs, servers, API keys, and prompts. We do it for you, delivering a ready-to-use result.",
      q4: 'What if something breaks?',
      a4: "30 days of tech support are included. If anything goes wrong, we'll fix it quickly and for free.",
      q5: 'What devices does it work on?',
      a5: "The agent can run on Mac, Windows, Linux, or a cloud server. We'll help you pick the best option during the free consultation.",
      q6: 'Do I need to be tech-savvy?',
      a6: 'Absolutely not. After our setup, you just talk to the agent via voice or text in Telegram, just like a normal person.'
    },
    cta: {
      title1: 'Your AI agent could be working',
      title2: 'right now.',
      subtitle: "Leave a request, and we'll get back to you to discuss setting up your personal AI employee.",
      s1: 'Leave a request',
      s1d: 'Fill out the form on the right',
      s2: 'Free audit',
      s2d: 'We hop on a call and break down your tasks',
      s3: 'Turnkey setup',
      s3d: 'We configure the agent and onboard you',
      formTitle: 'Free Consultation',
      formSubtitle: 'Fast response. 100% confidential.'
    },
    form: {
      name: 'Name',
      namePh: 'John Doe',
      phone: 'Phone',
      phonePh: '+1 (234) 567-8900',
      email: 'Email',
      emailPh: 'john@example.com',
      comment: 'Comment (optional)',
      commentPh: 'Briefly describe your tasks...',
      submit: 'Submit Request',
      terms: 'By clicking the button, you agree to the privacy policy.',
      success: 'Request sent successfully!'
    },
    footer: {
      desc: 'Independent integration studio. OpenClaw is an open-source project.'
    },
    badges: {
      conf: 'Confidential',
      fast: 'Fast response'
    }
  },
  ru: {
    seo: {
      title: 'Заказать установку OpenClaw под ключ — настройка ИИ-агента для бизнеса',
      description: 'Заказать установку OpenClaw под ключ для Telegram, WhatsApp, Slack, почты, календаря и файлов. Настройка ИИ-агента под задачи бизнеса, запуск и обучение.',
      h1: 'Заказать установку OpenClaw под ключ',
      h1sub: 'Настраиваем ИИ-агента OpenClaw для Telegram, WhatsApp, Slack, почты, календаря, браузера и файлов под задачи вашего бизнеса.'
    },
    nav: {
      features: 'Возможности',
      howItWorks: 'Как работаем',
      pricing: 'Цены',
      faq: 'FAQ',
      order: 'Заказать установку'
    },
    hero: {
      title1: 'Заказать установку',
      title2: 'OpenClaw',
      title3: 'под ключ',
      subtitle: 'Твой личный цифровой сотрудник в Telegram, WhatsApp или Slack — настроенный, обученный и готовый работать 24/7.',
      btn: 'Заказать установку ИИ-агента'
    },
    whatWeDo: {
      title1: 'Ты получаешь готового ИИ-агента.',
      title2: 'Мы берём на себя всё остальное.',
      withoutYou: 'Без тебя',
      withoutYouDesc: 'Читать сложную документацию, настраивать сервер окружение, разбираться с API ключами и писать системные промпты.',
      we: 'Мы',
      weDesc: 'Устанавливаем ядро, настраиваем интеграции, подключаем мессенджеры, обучаем агента под твои задачи и тестируем.',
      you: 'Ты',
      youDesc: 'Просто открываешь Telegram, пишешь своему новому сотруднику задачу — и он начинает работать.',
      link: 'Хочу готового агента'
    },
    features: {
      title1: 'Что OpenClaw сделает для тебя',
      title2: 'прямо сейчас',
      mail: 'Управление почтой',
      mailDesc: 'Агент сам читает, сортирует и готовит черновики ответов на рутинные письма.',
      cal: 'Календарь',
      calDesc: 'Планирует встречи, учитывая твой график.',
      web: 'Браузер',
      webDesc: 'Ищет инфу, парсит сайты и заполняет формы.',
      msg: 'Мессенджеры',
      msgDesc: 'Установка ИИ-агента для Telegram, WhatsApp, Slack и Discord. Общайся с агентом как с коллегой.',
      files: 'Файлы',
      filesDesc: 'Анализ PDF, таблиц и создание отчетов.',
      home: 'Умный дом',
      homeDesc: 'Управление светом и сценариями.',
      link: 'Заказать установку OpenClaw'
    },
    how: {
      title1: 'От заявки до работающего агента —',
      title2: 'под ключ',
      s1: 'Ты оставляешь заявку',
      s1d: 'Заполняешь короткую форму или пишешь нам в Telegram.',
      s2: 'Бесплатная консультация',
      s2d: 'Мы созваниваемся, чтобы понять твои задачи и процессы.',
      s3: 'Настройка OpenClaw для бизнеса',
      s3d: 'Разворачиваем OpenClaw, подключаем нужные каналы и обучаем агента.',
      s4: 'Сдача и обучение',
      s4d: 'Передаём готового агента, показываем как пользоваться и даём 30 дней поддержки.',
      link: 'Заказать установку'
    },
    pricing: {
      title1: 'Одно решение. Один платёж.',
      title2: 'Навсегда твой агент.',
      free: 'Бесплатно',
      freeDesc: 'консультация и аудит процессов',
      f1: 'Созвон с экспертом',
      f2: 'Анализ твоих рутинных задач',
      f3: 'Подбор оптимальных сценариев для ИИ',
      f4: 'Оценка технической возможности',
      f5: 'Ответы на все вопросы по безопасности',
      btnFree: 'Записаться',
      paid: 'Установка под ключ',
      paidFrom: 'от',
      paidDesc: 'единоразово, без скрытых подписок',
      p1: 'Полная установка OpenClaw на твой сервер/ПК',
      p2: 'Настройка под твои конкретные бизнес-задачи',
      p3: 'Подключение мессенджеров (Telegram / WhatsApp)',
      p4: 'Установка и настройка нужных Skills (навыков)',
      p5: 'Обучение — ты сразу умеешь пользоваться',
      p6: '30 дней технической поддержки после установки',
      btnPaid: 'Заказать установку'
    },
    testimonials: {
      title: 'Пользователи OpenClaw о своём агенте',
      link: 'Хочешь так же? Мы установим тебе такого же.',
      t1: 'После стольких лет хайпа вокруг ИИ... ИИ как напарник, а не просто инструмент. Наконец-то что-то, что реально делает работу, а не просто генерирует текст.',
      a1: 'Михаил',
      h1: '@mikhail_dev',
      t2: 'Больше не нужно платить виртуальному ассистенту!! OpenClaw безупречно справляется с моей почтой и расписанием. Лучшая инвестиция в этом году.',
      a2: 'Анна',
      h2: '@anna_biz',
      t3: 'Всё то, чем должна была стать Siri. Я просто пишу своему агенту в Telegram, и задачи выполняются. Это магия.',
      a3: 'Алекс',
      h3: '@alex_builds'
    },
    faq: {
      title: 'Частые вопросы',
      link: 'Остались вопросы? Заказать установку',
      q1: 'Это безопасно? Мои данные не утекут?',
      a1: 'OpenClaw работает локально или на твоём личном сервере. Данные не покидают твой контур и не используются для обучения чужих моделей.',
      q2: 'Нужна ли подписка на OpenClaw?',
      a2: 'Нет. OpenClaw — это полностью бесплатный open-source проект. Ты платишь только за API используемых нейросетей (копейки) и нашу единоразовую настройку.',
      q3: 'Почему не настроить самому?',
      a3: 'Можно. Но это 10-40 часов разбирательств с документацией, серверами, API ключами и промптами. Мы делаем это за тебя, выдавая готовый результат.',
      q4: 'Что если что-то сломается?',
      a4: 'В стоимость включены 30 дней технической поддержки. Если что-то пойдёт не так — мы оперативно и бесплатно всё починим.',
      q5: 'На каком устройстве работает?',
      a5: 'Агент может работать на Mac, Windows, Linux или облачном сервере. Мы подберём оптимальный вариант на бесплатной консультации.',
      q6: 'Нужно ли быть технарём?',
      a6: 'Абсолютно нет. После нашей настройки ты будешь общаться с агентом просто голосовыми или текстом в Telegram, как с обычным человеком.'
    },
    cta: {
      title1: 'Твой ИИ-агент мог бы работать',
      title2: 'прямо сейчас.',
      subtitle: 'Оставь заявку, и мы свяжемся с тобой, чтобы обсудить установку твоего личного ИИ-сотрудника.',
      s1: 'Оставляешь заявку',
      s1d: 'Заполняешь форму справа',
      s2: 'Бесплатный аудит',
      s2d: 'Созваниваемся и разбираем твои задачи',
      s3: 'Установка под ключ',
      s3d: 'Настраиваем агента и обучаем тебя',
      formTitle: 'Бесплатная консультация',
      formSubtitle: 'Отвечаем быстро. Конфіденциально.'
    },
    form: {
      name: 'Имя',
      namePh: 'Иван Иванов',
      phone: 'Телефон',
      phonePh: '+1 (234) 567-8900',
      email: 'Email',
      emailPh: 'ivan@example.com',
      comment: 'Комментарий (по желанию)',
      commentPh: 'Опишите ваши задачи вкратце...',
      submit: 'Отправить заявку',
      terms: 'Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности',
      success: 'Заявка успешно отправлена!'
    },
    footer: {
      desc: 'Независимая студия интеграции. OpenClaw является open-source проектом.'
    },
    badges: {
      conf: 'Конфиденциально',
      fast: 'Быстрый ответ'
    }
  },
  uk: {
    seo: {
      title: 'Замовити встановлення OpenClaw під ключ — налаштування ШІ-агента для бізнесу',
      description: 'Замовити встановлення OpenClaw під ключ для Telegram, WhatsApp, Slack, пошти, календаря та файлів. Налаштування ШІ-агента під задачі бізнесу, запуск і навчання.',
      h1: 'Замовити встановлення OpenClaw під ключ',
      h1sub: 'Налаштовуємо ШІ-агента OpenClaw для Telegram, WhatsApp, Slack, пошти, календаря, браузера та файлів під задачі вашого бізнесу.'
    },
    nav: {
      features: 'Можливості',
      howItWorks: 'Як працюємо',
      pricing: 'Ціни',
      faq: 'FAQ',
      order: 'Замовити встановлення'
    },
    hero: {
      title1: 'Замовити встановлення',
      title2: 'OpenClaw',
      title3: 'під ключ',
      subtitle: 'Твій особистий цифровий співробітник у Telegram, WhatsApp або Slack — налаштований, навчений і готовий працювати 24/7.',
      btn: 'Замовити встановлення ШІ-агента'
    },
    whatWeDo: {
      title1: 'Ти отримуєш готового ШІ-агента.',
      title2: 'Ми беремо на себе все інше.',
      withoutYou: 'Без тебе',
      withoutYouDesc: 'Читати складну документацію, налаштовувати серверне оточення, розбиратися з API ключами та писати системні промпти.',
      we: 'Ми',
      weDesc: 'Встановлюємо ядро, налаштовуємо інтеграції, підключаємо месенджери, навчаємо агента під твої завдання та тестуємо.',
      you: 'Ти',
      youDesc: 'Просто відкриваєш Telegram, пишеш своєму новому співробітнику завдання — і він починає працювати.',
      link: 'Хочу готового агента'
    },
    features: {
      title1: 'Що OpenClaw зробить для тебе',
      title2: 'прямо зараз',
      mail: 'Керування поштою',
      mailDesc: 'Агент сам читає, сортує та готує чернетки відповідей на рутинні листи.',
      cal: 'Календар',
      calDesc: 'Планує зустрічі, враховуючи твій графік.',
      web: 'Браузер',
      webDesc: 'Шукає інфу, парсить сайти та заповнює форми.',
      msg: 'Месенджери',
      msgDesc: 'Встановлення ШІ-агента для Telegram, WhatsApp, Slack та Discord. Спілкуйся з агентом як із колегою.',
      files: 'Файлы',
      filesDesc: 'Аналіз PDF, таблиць та створення звітів.',
      home: 'Розумний дім',
      homeDesc: 'Керування світлом та сценаріями.',
      link: 'Замовити встановлення OpenClaw'
    },
    how: {
      title1: 'Від заявки до працюючого агента —',
      title2: 'під ключ',
      s1: 'Ти залишаєш заявку',
      s1d: 'Заповнюєш коротку форму або пишеш нам у Telegram.',
      s2: 'Безкоштовна консультация',
      s2d: 'Ми зідзвонюємося, щоб зрозуміти твої завдання та процеси.',
      s3: 'Налаштування OpenClaw для бізнесу',
      s3d: 'Розгортаємо OpenClaw, підключаємо потрібні канали та навчаємо агента.',
      s4: 'Здача та навчання',
      s4d: 'Передаємо готового агента, показуємо як користуватися і даємо 30 днів підтримки.',
      link: 'Замовити встановлення'
    },
    pricing: {
      title1: 'Одне рішення. Один платіж.',
      title2: 'Назавжди твій агент.',
      free: 'Безкоштовно',
      freeDesc: 'консультація та аудит процесів',
      f1: 'Зідзвон з експертом',
      f2: 'Аналіз твоїх рутинних завдань',
      f3: 'Підбір оптимальних сценаріїв для ШІ',
      f4: 'Оцінка технічної можливості',
      f5: 'Відповіді на всі питання щодо безпеки',
      btnFree: 'Записатися',
      paid: 'Встановлення під ключ',
      paidFrom: 'від',
      paidDesc: 'одноразово, без прихованих підписок',
      p1: 'Повне встановлення OpenClaw на твій сервер/ПК',
      p2: 'Налаштування під твої конкретні бізнес-завдання',
      p3: 'Підключення месенджерів (Telegram / WhatsApp)',
      p4: 'Встановлення та налаштування потрібних Skills (навичок)',
      p5: 'Навчання — ти відразу вмієш користуватися',
      p6: '30 днів технічної підтримки після встановлення',
      btnPaid: 'Замовити встановлення'
    },
    testimonials: {
      title: 'Користувачі OpenClaw про свого агента',
      link: 'Хочеш так само? Ми встановимо тобі такого ж.',
      t1: 'Після стількох років хайпу навколо ШІ... ШІ як напарник, а не просто інструмент. Нарешті щось, що реально робить роботу, а не просто генерує текст.',
      a1: 'Михайло',
      h1: '@mykhailo_dev',
      t2: 'Більше не потрібно платити віртуальному асистенту!! OpenClaw бездоганно справляється з моєю поштою та розкладом. Найкраща інвестиція цього року.',
      a2: 'Ганна',
      h2: '@hanna_biz',
      t3: 'Все те, чим мала стати Siri. Я просто пишу своєму агенту в Telegram, і завдання виконуються. Це магія.',
      a3: 'Алекс',
      h3: '@alex_builds'
    },
    faq: {
      title: 'Часті запитання',
      link: 'Залишилися питання? Замовити встановлення',
      q1: 'Це безпечно? Мої дані не витечуть?',
      a1: 'OpenClaw працює локально або на твоєму особистому сервері. Дані не залишають твій контур і не використовуються для навчання чужих моделей.',
      q2: 'Чи потрібна підписка на OpenClaw?',
      a2: 'Ні. OpenClaw — це повністю безкоштовний open-source проєкт. Ти платиш тільки за API використовуваних нейромереж (копійки) та наше одноразове налаштування.',
      q3: 'Чому не налаштувати самому?',
      a3: 'Можна. Але це 10-40 годин розбирань з документацією, серверами, API ключами та промптами. Ми робимо це за тебе, видаючи готовий результат.',
      q4: 'Що якщо щось зламається?',
      a4: 'У вартість включено 30 днів технічної підтримки. Якщо щось піде не так — ми оперативно і безкоштовно все полагодимо.',
      q5: 'На якому пристрої працює?',
      a5: 'Агент може працювати на Mac, Windows, Linux або хмарному сервері. Ми підберемо оптимальний варіант на безкоштовній консультації.',
      q6: 'Чи потрібно бути технарем?',
      a6: 'Абсолютно ні. Після нашого налаштування ти будеш спілкуватися з агентом просто голосовими або текстом у Telegram, як зі звичайною людиною.'
    },
    cta: {
      title1: 'Твій ШІ-агент міг би працювати',
      title2: 'прямо зараз.',
      subtitle: "Залиш заявку, і ми зв'яжемося з тобою, щоб обговорити встановлення твого особистого ШІ-співробітника.",
      s1: 'Залишаєш заявку',
      s1d: 'Заповнюєш форму праворуч',
      s2: 'Безкоштовний аудит',
      s2d: 'Зідзвонюємося і розбираємо твої завдання',
      s3: 'Встановлення під ключ',
      s3d: 'Налаштовуємо агента і навчаємо тебе',
      formTitle: 'Безкоштовна консультація',
      formSubtitle: 'Відповідаємо швидко. Конфіденційно.'
    },
    form: {
      name: "Ім'я",
      namePh: 'Іван Іванов',
      phone: 'Телефон',
      phonePh: '+1 (234) 567-8900',
      email: 'Email',
      emailPh: 'ivan@example.com',
      comment: 'Коментар (за бажанням)',
      commentPh: 'Опишіть ваші завдання коротко...',
      submit: 'Відправити заявку',
      terms: 'Натискаючи кнопку, ви погоджуєтеся з політикою конфіденційності',
      success: 'Заявку успішно відправлено!'
    },
    footer: {
      desc: 'Незалежна студія інтеграції. OpenClaw є open-source проєктом.'
    },
    badges: {
      conf: 'Конфіденційно',
      fast: 'Швидка відповідь'
    }
  }
};
type Lang = 'en' | 'ru' | 'uk';
// --- SEO Hook ---
const useSEO = (lang: Lang, t: any, faqs: any[]) => {
  useEffect(() => {
    // 1. Title & Meta Description
    document.title = t.seo.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t.seo.description);
    // Robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'index, follow');
    // 2. HTML Lang
    document.documentElement.lang = lang === 'uk' ? 'uk' : lang;
    // 3. Canonical & Hreflang
    const baseUrl = 'https://setupclaw.agency';
    const currentPath = lang === 'en' ? '/' : lang === 'ru' ? '/ru' : '/ua';
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${baseUrl}${currentPath}`);
    // Remove old hreflangs
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    const langs = [{
      code: 'en',
      path: '/'
    }, {
      code: 'ru',
      path: '/ru'
    }, {
      code: 'uk',
      path: '/ua'
    }, {
      code: 'x-default',
      path: '/'
    }];
    langs.forEach(({
      code,
      path
    }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', code);
      link.setAttribute('href', `${baseUrl}${path}`);
      document.head.appendChild(link);
    });
    // 4. Schema Markup
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'SetupClaw Agency',
      url: baseUrl,
      logo: `${baseUrl}/logo.png`
    };
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: t.seo.h1,
      description: t.seo.description,
      provider: {
        '@type': 'Organization',
        name: 'SetupClaw Agency'
      },
      offers: {
        '@type': 'Offer',
        price: '500',
        priceCurrency: 'USD'
      }
    };
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f: any) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    };
    const injectSchema = (schema: any) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    };
    injectSchema(organizationSchema);
    injectSchema(serviceSchema);
    injectSchema(faqSchema);
  }, [lang, t, faqs]);
};
// --- Components ---
// Standard FadeIn for most elements
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 15
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  amount: 0.1
}} transition={{
  duration: 0.5,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
// Special FadeIn for Grid items to prevent layout shifts and jitter on mobile
const BentoFadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  scale: 0.98
}} whileInView={{
  opacity: 1,
  scale: 1
}} viewport={{
  once: true,
  amount: 0.1
}} transition={{
  duration: 0.5,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const LeadForm = ({
  onSubmit,
  t
}: {
  onSubmit?: () => void;
  t: any;
}) => {
  return <form className="space-y-4" onSubmit={e => {
    e.preventDefault();
    onSubmit?.();
  }} data-id="element-2">
      <div data-id="element-3">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-4">
          {t.name}
        </label>
        <input type="text" required className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all" placeholder={t.namePh} data-id="element-5" />
      </div>
      <div data-id="element-6">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-7">
          {t.phone}
        </label>
        <input type="tel" required className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all" placeholder={t.phonePh} data-id="element-8" />
      </div>
      <div data-id="element-9">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-10">
          {t.email}
        </label>
        <input type="email" required className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all" placeholder={t.emailPh} data-id="element-11" />
      </div>
      <div data-id="element-12">
        <label className="block text-sm font-medium text-zinc-400 mb-1.5" data-id="element-13">
          {t.comment}
        </label>
        <textarea rows={3} className="w-full bg-zinc-900/50 border border-white/20 rounded-xl px-4 py-3 text-base text-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all resize-none" placeholder={t.commentPh} data-id="element-14" />
      </div>
      <button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgba(220,38,38,0.2)] mt-2" data-id="element-15">
        {t.submit}
      </button>
      <p className="text-xs text-zinc-500 text-center mt-4" data-id="element-16">{t.terms}</p>
    </form>;
};
const LandingPage = ({
  lang
}: {
  lang: Lang;
}) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const t = translations[lang];
  const faqs = [{
    q: t.faq.q1,
    a: t.faq.a1
  }, {
    q: t.faq.q2,
    a: t.faq.a2
  }, {
    q: t.faq.q3,
    a: t.faq.a3
  }, {
    q: t.faq.q4,
    a: t.faq.a4
  }, {
    q: t.faq.q5,
    a: t.faq.a5
  }, {
    q: t.faq.q6,
    a: t.faq.a6
  }];
  useSEO(lang, t, faqs);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);
  const openModal = () => {
    setIsModalOpen(true);
    setMobileMenuOpen(false);
  };
  const handleLangChange = (newLang: Lang) => {
    if (newLang === 'en') navigate('/');
    if (newLang === 'ru') navigate('/ru');
    if (newLang === 'uk') navigate('/ua');
    setMobileMenuOpen(false);
  };
  const steps = [{
    num: '1',
    title: t.how.s1,
    desc: t.how.s1d
  }, {
    num: '2',
    title: t.how.s2,
    desc: t.how.s2d
  }, {
    num: '3',
    title: t.how.s3,
    desc: t.how.s3d
  }, {
    num: '4',
    title: t.how.s4,
    desc: t.how.s4d
  }];
  const testimonials = [{
    text: t.testimonials.t1,
    author: t.testimonials.a1,
    handle: t.testimonials.h1
  }, {
    text: t.testimonials.t2,
    author: t.testimonials.a2,
    handle: t.testimonials.h2
  }, {
    text: t.testimonials.t3,
    author: t.testimonials.a3,
    handle: t.testimonials.h3
  }];
  return <div className="min-h-screen bg-black text-zinc-50 font-sans selection:bg-red-500/30 overflow-x-hidden" data-id="element-17">
      {/* Navbar */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${isScrolled ? 'bg-black/80 backdrop-blur-md border-white/10 py-3' : 'bg-transparent border-transparent py-5'}`} data-id="element-18">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-19">
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight" data-id="element-20">
            <span className="text-2xl" data-id="element-21">🦞</span>
            <span data-id="element-22">SetupClaw Agency</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-400" data-id="element-23">
            <a href="#features" className="hover:text-white transition-colors" data-id="element-24">
              {t.nav.features}
            </a>
            <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-25">
              {t.nav.howItWorks}
            </a>
            <a href="#pricing" className="hover:text-white transition-colors" data-id="element-26">
              {t.nav.pricing}
            </a>
            <a href="#faq" className="hover:text-white transition-colors" data-id="element-27">
              {t.nav.faq}
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-6" data-id="element-28">
            {/* Language Switcher */}
            <div className="relative group" data-id="element-29">
              <button className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-medium py-2" data-id="element-30">
                <Globe className="w-4 h-4" data-id="element-31" />
                <span className="uppercase text-sm" data-id="element-32">
                  {lang === 'uk' ? 'UA' : lang}
                </span>
                <ChevronDown className="w-3 h-3" data-id="element-33" />
              </button>
              <div className="absolute top-full right-0 mt-1 w-24 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-xl" data-id="element-34">
                <button onClick={() => handleLangChange('en')} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === 'en' ? 'text-white font-bold bg-white/5' : 'text-zinc-400'}`} data-id="element-35">
                  EN
                </button>
                <button onClick={() => handleLangChange('ru')} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === 'ru' ? 'text-white font-bold bg-white/5' : 'text-zinc-400'}`} data-id="element-36">
                  RU
                </button>
                <button onClick={() => handleLangChange('uk')} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition-colors ${lang === 'uk' ? 'text-white font-bold bg-white/5' : 'text-zinc-400'}`} data-id="element-37">
                  UA
                </button>
              </div>
            </div>

            <button onClick={openModal} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(220,38,38,0.3)]" data-id="element-38">
              {t.nav.order}
            </button>
          </div>

          {/* Mobile Toggle */}
          <div className="md:hidden flex items-center gap-4" data-id="element-39">
            <div className="relative group" data-id="element-40">
              <button className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors font-medium" data-id="element-41">
                <span className="uppercase text-sm" data-id="element-42">
                  {lang === 'uk' ? 'UA' : lang}
                </span>
              </button>
              <div className="absolute top-full right-0 mt-2 w-24 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all" data-id="element-43">
                <button onClick={() => handleLangChange('en')} className={`w-full text-left px-4 py-2 text-sm ${lang === 'en' ? 'text-white font-bold' : 'text-zinc-400'}`} data-id="element-44">
                  EN
                </button>
                <button onClick={() => handleLangChange('ru')} className={`w-full text-left px-4 py-2 text-sm ${lang === 'ru' ? 'text-white font-bold' : 'text-zinc-400'}`} data-id="element-45">
                  RU
                </button>
                <button onClick={() => handleLangChange('uk')} className={`w-full text-left px-4 py-2 text-sm ${lang === 'uk' ? 'text-white font-bold' : 'text-zinc-400'}`} data-id="element-46">
                  UA
                </button>
              </div>
            </div>
            <button className="text-zinc-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-47">
              <Menu className="w-6 h-6" data-id="element-48" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-49">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-black border-b border-white/10 overflow-hidden" data-id="element-50">
              <div className="flex flex-col px-6 py-4 gap-4" data-id="element-51">
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-52">
                  {t.nav.features}
                </a>
                <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-53">
                  {t.nav.howItWorks}
                </a>
                <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-54">
                  {t.nav.pricing}
                </a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300" data-id="element-55">
                  {t.nav.faq}
                </a>
                <button onClick={openModal} className="bg-red-600 text-white px-4 py-3 rounded-lg font-medium w-full mt-2" data-id="element-56">
                  {t.nav.order}
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6 relative overflow-hidden" data-id="element-57">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[800px] md:h-[800px] bg-red-600/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-58" />

        <div className="container mx-auto max-w-4xl text-center relative z-10" data-id="element-59">
          <FadeIn data-id="element-60">
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-[1.1]" data-id="element-61">
              {t.hero.title1}{' '}
              <span className="text-red-500" data-id="element-62">{t.hero.title2}</span>{' '}
              {t.hero.title3}
            </h1>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-63">
            <p className="text-xl md:text-2xl text-zinc-400 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-64">
              {t.seo.h1sub}
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="flex flex-col items-center gap-4" data-id="element-65">
            <button onClick={openModal} className="group relative inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(220,38,38,0.4)]" data-id="element-66">
              <span className="text-2xl" data-id="element-67">🦞</span>
              {t.hero.btn}
            </button>
          </FadeIn>
        </div>
      </section>

      {/* What we do */}
      <section className="py-24 border-y border-white/10 px-6 relative overflow-hidden" data-id="element-68">
        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-69">
          <FadeIn data-id="element-70">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16 max-w-4xl mx-auto leading-tight" data-id="element-71">
              {t.whatWeDo.title1}
              <br data-id="element-72" />
              <span className="text-zinc-500" data-id="element-73">{t.whatWeDo.title2}</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-74">
            <FadeIn delay={0.1} className="bg-black p-8 rounded-3xl border border-red-500/40 hover:border-red-400 transition-colors duration-300 relative overflow-hidden group" data-id="element-75">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-76" />
              <div className="relative z-10" data-id="element-77">
                <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-6 border border-white/10 group-hover:border-red-500/30 transition-colors" data-id="element-78">
                  <X className="w-7 h-7 text-red-500" data-id="element-79" />
                </div>
                <h3 className="text-xl font-bold mb-4" data-id="element-80">
                  {t.whatWeDo.withoutYou}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-81">
                  {t.whatWeDo.withoutYouDesc}
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-black p-8 rounded-3xl border border-red-500/40 hover:border-red-400 transition-colors duration-300 relative overflow-hidden shadow-[0_0_30px_rgba(220,38,38,0.05)] group" data-id="element-82">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/10 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500" data-id="element-83" />
              <div className="relative z-10" data-id="element-84">
                <div className="w-14 h-14 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 border border-red-500/20" data-id="element-85">
                  <Check className="w-7 h-7 text-red-500" data-id="element-86" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-white" data-id="element-87">
                  {t.whatWeDo.we}
                </h3>
                <p className="text-zinc-300 leading-relaxed" data-id="element-88">
                  {t.whatWeDo.weDesc}
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-black p-8 rounded-3xl border border-red-500/40 hover:border-red-400 transition-colors duration-300 relative overflow-hidden group" data-id="element-89">
              <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-90" />
              <div className="relative z-10" data-id="element-91">
                <div className="w-14 h-14 bg-zinc-900 rounded-2xl flex items-center justify-center mb-6 border border-white/10 group-hover:border-red-500/30 transition-colors" data-id="element-92">
                  <span className="text-2xl" data-id="element-93">🎯</span>
                </div>
                <h3 className="text-xl font-bold mb-4" data-id="element-94">{t.whatWeDo.you}</h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-95">
                  {t.whatWeDo.youDesc}
                </p>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.4} className="mt-12 text-center" data-id="element-96">
            <button onClick={openModal} className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-semibold text-lg transition-colors group" data-id="element-97">
              {t.whatWeDo.link}{' '}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-98" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Features (Bento Grid) */}
      <section id="features" className="py-24 px-6 relative overflow-hidden" data-id="element-99">
        <div className="container mx-auto max-w-5xl relative z-10" data-id="element-100">
          <FadeIn data-id="element-101">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-102">
              {t.features.title1}{' '}
              <span className="text-red-500" data-id="element-103">{t.features.title2}</span>
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6" data-id="element-104">
            <BentoFadeIn delay={0.1} className="md:col-span-3 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-105">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-106" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-107">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-108">
                  <Mail className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-109" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-110">
                  {t.features.mail}
                </h3>
                <p className="text-zinc-400 leading-relaxed max-w-md" data-id="element-111">
                  {t.features.mailDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.2} className="md:col-span-2 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-112">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-113" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-114">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-115">
                  <Calendar className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-116" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-117">
                  {t.features.cal}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-118">
                  {t.features.calDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.3} className="md:col-span-2 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-119">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-120" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-121">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-122">
                  <Globe className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-123" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-124">
                  {t.features.web}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-125">
                  {t.features.webDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.4} className="md:col-span-3 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-126">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-127" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-128">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-129">
                  <MessageSquare className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-130" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-131">
                  {t.features.msg}
                </h3>
                <p className="text-zinc-400 leading-relaxed max-w-md" data-id="element-132">
                  {t.features.msgDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.5} className="md:col-span-3 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-133">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-134" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-135">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-136">
                  <FileText className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-137" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-138">
                  {t.features.files}
                </h3>
                <p className="text-zinc-400 leading-relaxed max-w-md" data-id="element-139">
                  {t.features.filesDesc}
                </p>
              </div>
            </BentoFadeIn>

            <BentoFadeIn delay={0.6} className="md:col-span-2 bg-zinc-950 border border-red-500/40 p-8 rounded-3xl hover:border-red-400 transition-all duration-300 group relative overflow-hidden" data-id="element-140">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-141" />
              <div className="relative z-10 h-full flex flex-col" data-id="element-142">
                <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-white/10 flex items-center justify-center mb-6 group-hover:bg-red-500/10 group-hover:border-red-500/30 transition-all" data-id="element-143">
                  <Home className="w-7 h-7 text-zinc-400 group-hover:text-red-400 transition-colors" data-id="element-144" />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-145">
                  {t.features.home}
                </h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-146">
                  {t.features.homeDesc}
                </p>
              </div>
            </BentoFadeIn>
          </div>

          <FadeIn delay={0.7} className="mt-20 text-center" data-id="element-147">
            <button onClick={openModal} className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center gap-2" data-id="element-148">
              {t.features.link} <ArrowRight className="w-5 h-5" data-id="element-149" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* How we work */}
      <section id="how-it-works" className="py-24 border-y border-white/10 px-6 relative overflow-hidden" data-id="element-150">
        <div className="absolute top-1/2 left-0 w-[250px] h-[250px] md:w-[500px] md:h-[500px] bg-red-600/5 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-151" />
        <div className="container mx-auto max-w-4xl relative z-10" data-id="element-152">
          <FadeIn data-id="element-153">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-154">
              {t.how.title1}{' '}
              <span className="text-red-500" data-id="element-155">{t.how.title2}</span>
            </h2>
          </FadeIn>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-7 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-red-500/40 before:to-transparent" data-id="element-156">
            {steps.map((step, idx) => <FadeIn key={idx} delay={idx * 0.1} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-157">
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl border-4 border-black bg-red-600 text-white font-bold text-xl shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-[0_0_0_4px_rgba(0,0,0,1)] z-10" data-id="element-158">
                  {step.num}
                </div>
                <div className="w-[calc(100%-4.5rem)] md:w-[calc(50%-3.5rem)] p-6 rounded-3xl bg-black border border-red-500/40 group-hover:border-red-400 transition-colors duration-300 relative overflow-hidden" data-id="element-159">
                  <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-160" />
                  <div className="relative z-10" data-id="element-161">
                    <h3 className="font-bold text-xl mb-2 text-white" data-id="element-162">
                      {step.title}
                    </h3>
                    <p className="text-zinc-400" data-id="element-163">{step.desc}</p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-164">
            <button onClick={openModal} className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105" data-id="element-165">
              {t.how.link}
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 relative overflow-hidden" data-id="element-166">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-red-600/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-167" />

        <div className="container mx-auto max-w-5xl relative z-10" id="order" data-id="element-168">
          <FadeIn data-id="element-169">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-170">
              {t.pricing.title1}
              <br data-id="element-171" />
              <span className="text-red-500" data-id="element-172">{t.pricing.title2}</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-173">
            {/* Free Tier */}
            <FadeIn delay={0.1} data-id="element-174">
              <div className="bg-black rounded-3xl border border-red-500/40 p-8 md:p-10 shadow-[0_0_50px_rgba(220,38,38,0.02)] relative overflow-hidden group hover:border-red-400 transition-all duration-500 h-full flex flex-col" data-id="element-175">
                <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-176" />

                <div className="relative z-10 flex-grow" data-id="element-177">
                  <div className="mb-8" data-id="element-178">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-zinc-900 rounded-2xl mb-6 border border-white/10 group-hover:border-red-500/30 transition-colors" data-id="element-179">
                      <span className="text-2xl" data-id="element-180">💬</span>
                    </div>
                    <h3 className="text-2xl font-bold text-zinc-400 tracking-wide uppercase mb-2" data-id="element-181">
                      {t.pricing.free}
                    </h3>
                    <div className="flex items-baseline gap-2" data-id="element-182">
                      <span className="text-5xl font-extrabold text-white" data-id="element-183">
                        $0
                      </span>
                    </div>
                    <p className="text-zinc-500 mt-2 font-medium" data-id="element-184">
                      {t.pricing.freeDesc}
                    </p>
                  </div>

                  <div className="space-y-4 mb-10" data-id="element-185">
                    {[t.pricing.f1, t.pricing.f2, t.pricing.f3, t.pricing.f4, t.pricing.f5].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-186">
                        <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0 border border-red-500/20" data-id="element-187">
                          <Check className="w-4 h-4 text-red-500" data-id="element-188" />
                        </div>
                        <span className="text-zinc-300" data-id="element-189">{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="relative z-10 mt-auto" data-id="element-190">
                  <button onClick={openModal} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_30px_rgba(220,38,38,0.3)] flex items-center justify-center gap-2" data-id="element-191">
                    {t.pricing.btnFree}
                  </button>
                </div>
              </div>
            </FadeIn>

            {/* Paid Tier */}
            <FadeIn delay={0.2} data-id="element-192">
              <div className="bg-black rounded-3xl border border-red-500/40 p-8 md:p-10 shadow-[0_0_50px_rgba(220,38,38,0.05)] relative overflow-hidden group hover:border-red-400 transition-all duration-500 h-full flex flex-col" data-id="element-193">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/20 via-black to-black opacity-60 pointer-events-none transition-opacity duration-500 group-hover:opacity-100" data-id="element-194" />

                <div className="relative z-10 flex-grow" data-id="element-195">
                  <div className="mb-8" data-id="element-196">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-red-500/10 rounded-2xl mb-6 border border-red-500/20" data-id="element-197">
                      <span className="text-2xl" data-id="element-198">🦞</span>
                    </div>
                    <h3 className="text-2xl font-bold text-red-400 tracking-wide uppercase mb-2" data-id="element-199">
                      {t.pricing.paid}
                    </h3>
                    <div className="flex items-baseline gap-2" data-id="element-200">
                      <span className="text-zinc-400 text-xl" data-id="element-201">
                        {t.pricing.paidFrom}
                      </span>
                      <span className="text-5xl font-extrabold text-white" data-id="element-202">
                        $500
                      </span>
                    </div>
                    <p className="text-zinc-500 mt-2 font-medium" data-id="element-203">
                      {t.pricing.paidDesc}
                    </p>
                  </div>

                  <div className="space-y-4 mb-10" data-id="element-204">
                    {[t.pricing.p1, t.pricing.p2, t.pricing.p3, t.pricing.p4, t.pricing.p5, t.pricing.p6].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-205">
                        <div className="mt-1 bg-red-500/20 p-1 rounded-full shrink-0 border border-red-500/30" data-id="element-206">
                          <Check className="w-4 h-4 text-red-500" data-id="element-207" />
                        </div>
                        <span className="text-zinc-300" data-id="element-208">{item}</span>
                      </div>)}
                  </div>
                </div>

                <div className="relative z-10 mt-auto" data-id="element-209">
                  <button onClick={openModal} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_30px_rgba(220,38,38,0.3)] flex items-center justify-center gap-2" data-id="element-210">
                    <span className="text-xl" data-id="element-211">🦞</span> {t.pricing.btnPaid}
                  </button>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-y border-white/10 px-6 relative overflow-hidden" data-id="element-212">
        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-213">
          <FadeIn data-id="element-214">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-215">
              {t.testimonials.title}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-216">
            {testimonials.map((t_item, idx) => <FadeIn key={idx} delay={idx * 0.1} className="bg-black border border-red-500/40 p-8 rounded-3xl flex flex-col hover:border-red-400 transition-colors duration-300 relative overflow-hidden group" data-id="element-217">
                <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-218" />
                <div className="relative z-10 flex flex-col h-full" data-id="element-219">
                  <div className="flex items-center gap-4 mb-6" data-id="element-220">
                    <div className="w-14 h-14 bg-zinc-900 border border-white/10 rounded-2xl flex items-center justify-center font-bold text-xl text-zinc-400 group-hover:border-red-500/30 transition-colors" data-id="element-221">
                      {t_item.author[0].toUpperCase()}
                    </div>
                    <div data-id="element-222">
                      <div className="font-bold text-white" data-id="element-223">
                        {t_item.author}
                      </div>
                      <div className="text-zinc-500 text-sm" data-id="element-224">
                        {t_item.handle}
                      </div>
                    </div>
                  </div>
                  <p className="text-zinc-300 leading-relaxed flex-grow" data-id="element-225">
                    "{t_item.text}"
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-226">
            <p className="text-xl text-zinc-400 mb-6" data-id="element-227">{t.testimonials.link}</p>
            <button onClick={openModal} className="inline-flex items-center gap-2 text-white hover:text-red-400 font-semibold text-lg transition-colors group" data-id="element-228">
              {t.nav.order}{' '}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-229" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 relative overflow-hidden" data-id="element-230">
        <div className="container mx-auto max-w-3xl relative z-10" data-id="element-231">
          <FadeIn data-id="element-232">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-233">
              {t.faq.title}
            </h2>
          </FadeIn>

          <div className="space-y-4" data-id="element-234">
            {faqs.map((faq, idx) => <FadeIn key={idx} delay={idx * 0.05} data-id="element-235">
                <div className="border border-red-500/40 rounded-2xl bg-black overflow-hidden hover:border-red-400 transition-colors duration-300" data-id="element-236">
                  <button className="w-full px-6 py-5 text-left flex items-center justify-between font-semibold text-lg hover:bg-red-500/5 transition-colors text-white" onClick={() => setOpenFaq(openFaq === idx ? null : idx)} data-id="element-237">
                    <span data-id="element-238">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-239" />
                  </button>
                  <motion.div initial={false} animate={{
                height: openFaq === idx ? 'auto' : 0,
                opacity: openFaq === idx ? 1 : 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-240">
                    <div className="px-6 pb-5 text-zinc-400 leading-relaxed border-t border-red-500/40 pt-4" data-id="element-241">
                      {faq.a}
                    </div>
                  </motion.div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.3} className="mt-12 text-center" data-id="element-242">
            <button onClick={openModal} className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-semibold text-lg transition-colors group" data-id="element-243">
              {t.faq.link}{' '}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-244" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 md:px-6 bg-black relative overflow-hidden border-t border-white/10" data-id="element-245">
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-red-600/10 rounded-full blur-[60px] md:blur-[120px] pointer-events-none" data-id="element-246" />

        <div className="container mx-auto max-w-6xl relative z-10" data-id="element-247">
          <div className="bg-zinc-950 border border-red-500/40 rounded-3xl p-5 md:p-16 shadow-2xl overflow-hidden relative" data-id="element-248">
            <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent pointer-events-none" data-id="element-249" />

            <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center relative z-10" data-id="element-250">
              <FadeIn data-id="element-251">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-2xl mb-8 border border-red-500/20" data-id="element-252">
                  <span className="text-4xl" data-id="element-253">🦞</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight text-white" data-id="element-254">
                  {t.cta.title1}{' '}
                  <span className="text-red-500" data-id="element-255">{t.cta.title2}</span>
                </h2>
                <p className="text-zinc-400 text-lg mb-10 leading-relaxed" data-id="element-256">
                  {t.cta.subtitle}
                </p>

                <div className="space-y-6" data-id="element-257">
                  <div className="flex items-start gap-4" data-id="element-258">
                    <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center shrink-0" data-id="element-259">
                      <span className="text-zinc-400 font-bold" data-id="element-260">1</span>
                    </div>
                    <div data-id="element-261">
                      <h3 className="text-white font-bold mb-1" data-id="element-262">{t.cta.s1}</h3>
                      <p className="text-zinc-500 text-sm" data-id="element-263">{t.cta.s1d}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-264">
                    <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center shrink-0" data-id="element-265">
                      <span className="text-zinc-400 font-bold" data-id="element-266">2</span>
                    </div>
                    <div data-id="element-267">
                      <h3 className="text-white font-bold mb-1" data-id="element-268">{t.cta.s2}</h3>
                      <p className="text-zinc-500 text-sm" data-id="element-269">{t.cta.s2d}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-270">
                    <div className="w-12 h-12 rounded-2xl bg-black border border-white/10 flex items-center justify-center shrink-0" data-id="element-271">
                      <span className="text-zinc-400 font-bold" data-id="element-272">3</span>
                    </div>
                    <div data-id="element-273">
                      <h3 className="text-white font-bold mb-1" data-id="element-274">{t.cta.s3}</h3>
                      <p className="text-zinc-500 text-sm" data-id="element-275">{t.cta.s3d}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-276">
                <div className="bg-black border border-red-500/40 rounded-3xl p-5 md:p-8 shadow-[0_0_50px_rgba(220,38,38,0.05)] relative group hover:border-red-400 transition-colors duration-500" data-id="element-277">
                  <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl" data-id="element-278" />
                  <div className="relative z-10" data-id="element-279">
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-280">
                      {t.cta.formTitle}
                    </h3>
                    <p className="text-zinc-400 mb-6 text-sm" data-id="element-281">
                      {t.cta.formSubtitle}
                    </p>
                    <LeadForm onSubmit={() => alert(t.form.success)} t={t.form} data-id="element-282" />
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10 bg-black" data-id="element-283">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-284">
          <div className="flex items-center gap-2 font-bold text-xl text-white" data-id="element-285">
            <span data-id="element-286">🦞</span>
            <span data-id="element-287">SetupClaw Agency</span>
          </div>

          <div className="flex items-center gap-6" data-id="element-288">
            <a href="#" className="text-zinc-400 hover:text-white transition-colors font-medium" data-id="element-289">
              Telegram
            </a>
            <a href="#" className="text-zinc-400 hover:text-white transition-colors font-medium" data-id="element-290">
              WhatsApp
            </a>
          </div>

          <div className="text-zinc-600 text-sm text-center md:text-right max-w-xs" data-id="element-291">
            {t.footer.desc}
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-292">
        {isModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} data-id="element-293">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          opacity: 0,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-zinc-950 border border-red-500/40 rounded-3xl p-6 md:p-8 w-full max-w-md relative shadow-2xl" data-id="element-294">
              <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors" data-id="element-295">
                <X className="w-6 h-6" data-id="element-296" />
              </button>

              <div className="mb-8" data-id="element-297">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-298">
                  {t.cta.formTitle}
                </h3>
                <p className="text-zinc-400" data-id="element-299">{t.cta.subtitle}</p>
              </div>

              <LeadForm onSubmit={() => {
            alert(t.form.success);
            setIsModalOpen(false);
          }} t={t.form} data-id="element-300" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function App() {
  return <BrowserRouter data-id="element-301">
      <Routes data-id="element-302">
        <Route path="/" element={<LandingPage lang="en" data-id="element-304" />} data-id="element-303" />
        <Route path="/ru" element={<LandingPage lang="ru" data-id="element-306" />} data-id="element-305" />
        <Route path="/ua" element={<LandingPage lang="uk" data-id="element-308" />} data-id="element-307" />
        <Route path="*" element={<Navigate to="/" replace data-id="element-310" />} data-id="element-309" />
      </Routes>
    </BrowserRouter>;
}